import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  Coverage,
  ICoverageAddRequest,
  ICoverageUpdateRequest,
  IUpdateCoverageFormGroupValue,
  MemberDetails,
} from '../../../../interfaces/member.interface';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, defer, EMPTY, Observable, of } from 'rxjs';
import {
  ClientInfoResponse,
  GroupDetails,
} from '../../../../interfaces/UserGroup.interface';
import { LoaderService } from '../../../services/loader/loader.service';
import { EligibilityService } from '../../../../core/services/eligibility/eligibility.service';
import {
  catchError,
  finalize,
  map,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { formatDate, getErrorMessage } from '../../../../util';
import { ErrorComponent } from '../../../../core/components/error/error.component';
import { SaveAuditLogsRequestInterface } from 'src/app/interfaces/audit-logs.interface';
import { AuditLogsService } from 'src/app/core/services/audit-logs/audit-logs.service';
import { AppConstants, CoverageNoteType } from 'src/app/app.constants';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import {
  ErrorManagerService,
  ErrorType,
} from '../../../../core/services/error';
import { HttpErrorResponse } from '@angular/common/http';
import { AppCenterAnalytics } from '../../../../services/app-insights/app-insights';
import { AppInsightsService } from '../../../../services/app-insights/app-insights.service';
import { dateValidator } from 'src/app/shared/validators/date-validator';
import { IFutureCoverageRequest } from '@interfaces';

@Component({
  selector: 'app-update-coverage-details-dialog',
  templateUrl: './update-coverage-details-dialog.component.html',
  styleUrls: ['./update-coverage-details-dialog.component.scss'],
  standalone: false,
})
export class UpdateCoverageDetailsDialogComponent implements OnInit {
  private familyDetails$!: Observable<MemberDetails[]>
  public updateCoverageFormGroup!: FormGroup;
  public cardHolderRC: string = AppConstants.RELATIONSHIP_CODES_ALL.CARDHOLDER;
  public groups: string[] = ['Demo'];
  public listOfGroups$!: Observable<GroupDetails[]>;
  public filteredGroups!: Observable<GroupDetails[]>;
  public readonly coverageNoteTypes = CoverageNoteType;

  public displayedColumns: string[] = [
    'personCode',
    'name',
    'cardId',
    'relationshipCode',
    'startDate',
    'endDate',
  ];
  public displayGender = AppConstants.DISPLAY_GENDER;
  public initialFormGroupValue!: IUpdateCoverageFormGroupValue;
  public refreshDependents: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);
  updateRx = false;
  addRx = false;
  updateCoverage = false;
  addCoverage = false;
  updatedMember: Partial<MemberDetails> = {};
  selectedGroupName = '';
  dataSource!: MemberDetails[];
  dependents$!: Observable<MemberDetails[]>;
  public totalDependents!: number;
  public activeDependents: MemberDetails[] = [];
  groupInfo: { groupId: string; groupName: string } = {
    groupId: '',
    groupName: '',
  };
  clientInfo$!: Observable<ClientInfoResponse[]>;
  public selectedDependents: MemberDetails[] = [];
  public groupFilterControl = new FormControl();
  public startDateMin = null;
  public startDateMax = null;
  public maxDate = new Date('12/31/9999');
  public isSaving = false;
  public cardHolderDetails!: MemberDetails;
  public cardHoldersFutureCoverage!: Coverage;
  public cardHoldersCurrentCoverage!: Coverage;

  public headerMap: Record<string, string> = {
    updateCoverage: 'Edit Coverage',
    addCoverage: 'Add Coverage',
    addRx: 'Add Coverage Type',
    updateRx: 'Edit Coverage Type',
    addFutureCoverage: 'Add Future Coverage',
    updateFutureCoverage: 'Edit Future Coverage'
  };

  // Helper getters for template logic
  get isCardHolder(): boolean {
    return this.dialogData.memberDetails.relationshipCode === this.cardHolderRC;
  }
  // Determines whether to show coverage note based on action and coverage details
  get showCoverageNote(): boolean {
    return this.coverageNoteType !== null;
  }
  // Determines the type of coverage note to display based on action, relationship, and coverage details
  get coverageNoteType(): CoverageNoteType | null {
    const action = this.dialogData.action;

    if (
      this.isCardHolder &&
      (action === 'addFutureCoverage' || action === 'updateFutureCoverage') &&
      this.cardHoldersCurrentCoverage
    ) {
      return CoverageNoteType.CardholderFuture;
    }

    if (
      this.isCardHolder &&
      action === 'addCoverage' &&
      this.cardHoldersFutureCoverage
    ) {
      return CoverageNoteType.CardholderActiveAdd;
    }

    if (
      this.isCardHolder &&
      action === 'updateCoverage' &&
      this.cardHoldersFutureCoverage
    ) {
      return CoverageNoteType.CardholderActiveUpdate;
    }

    if (
      !this.isCardHolder &&
      (action === 'addFutureCoverage' || action === 'updateFutureCoverage') &&
      this.cardHoldersFutureCoverage
    ) {
      return CoverageNoteType.DependentFuture;
    }

    if (
      !this.isCardHolder &&
      (action === 'addCoverage' || action === 'updateCoverage') &&
      this.cardHoldersCurrentCoverage
    ) {
      return CoverageNoteType.DependentActive;
    }

    return null;
  }


  constructor(
    public dialogRef: MatDialogRef<UpdateCoverageDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private readonly formBuilder: FormBuilder,
    private readonly loader: LoaderService,
    private readonly eligibilityService: EligibilityService,
    private matDialog: MatDialog,
    private readonly auditLogsService: AuditLogsService,
    private readonly authService: AuthService,
    private readonly errorManager: ErrorManagerService,
    private readonly appInsightsService: AppInsightsService
  ) { }

  ngOnInit(): void {
    this.appInsightsService.trackPageView(AppCenterAnalytics.UPDATE_COVERAGE_DIALOG_VIEWED, { data: this.dialogData });
    if (
      (this.dialogData.action === 'addCoverage' || this.dialogData.action === 'addFutureCoverage') &&
      this.dialogData.memberDetails.relationshipCode === this.cardHolderRC
    ) {
      this.displayedColumns.unshift('select');
    }
    if (this.dialogData.action === 'addFutureCoverage' || this.dialogData.action === 'updateFutureCoverage') {
      // Removed startDate and endDate columns from displayedColumns
      const columnsToBeRemoved = ['startDate', 'endDate'];
      this.displayedColumns = this.displayedColumns.filter(
        col => !columnsToBeRemoved.includes(col)
      );

    }

    this.loader.showLoader();

    const query = this.eligibilityService.getSearchQuery({
      clientId: this.dialogData.memberDetails.clientId,
      cardId: this.dialogData.memberDetails.cardId,
    });

    this.familyDetails$ = this.eligibilityService
      .getMemberDetails(query)
      .pipe(
        take(1), // HTTP call – completes automatically
      );

    // Initialize dependents stream which relies on familyDetails$
    this.initDependentsStream();
    // Initialize cardholder details and related logic which also relies on familyDetails$
    this.initCardHolderDetails();

    const requiredValidator = Validators.required;
    this.updateCoverageFormGroup = this.formBuilder.group({
      groupName: ['', [requiredValidator]],
      startDate: ['', [requiredValidator, dateValidator()]],
      endDate: ['', [dateValidator()]],
    });
    this.updateCoverageFormGroup
      .get('startDate')
      .valueChanges.subscribe((startDate) => {
        // Update the end date filter when the start date changes
        this.updateCoverageFormGroup.get('endDate').updateValueAndValidity();
      });

    if (
      this.dialogData.action === 'addRx' ||
      this.dialogData.action === 'updateRx' ||
      this.dialogData.action === 'updateCoverage' ||
      this.dialogData.action === 'updateFutureCoverage'
    ) {
      this.updateCoverageFormGroup.controls.endDate.setValidators([
        Validators.required,
      ]);
    }

    if (this.dialogData.coverage.edit && this.dialogData.action !== 'addRx' && this.dialogData.action !== 'updateFutureCoverage') {
      this.updateCoverageFormGroup.controls.startDate.disable();
      this.updateCoverageFormGroup.controls.groupName.disable();
    }

    if (this.dialogData.coverage.edit) {

      this.populateFormGroup(this.dialogData.coverage);
    }
    this.getGroupsByClient(this.dialogData.memberDetails.clientId);

    this.clientInfo$ = this.eligibilityService
      .getClientDetailsForBenefit(this.dialogData.memberDetails.clientId)
      .pipe(
        tap((res) => {
          this.checkIfLoaderNeedsToBeEnded();
          let result = {};
          result = this.extractGroupInfo(res, result);
          this.groupInfo.groupId = result['groupId'] ? result['groupId'] : '';
          this.groupInfo.groupName = result['groupName']
            ? result['groupName']
            : '';
        })
      );
  }

  private initCardHolderDetails(): void {
    if (
      this.dialogData.memberDetails.relationshipCode === this.cardHolderRC ||
      !['addCoverage', 'updateCoverage', 'addFutureCoverage', 'updateFutureCoverage']
        .includes(this.dialogData.action)
    ) {
      return;
    }

    this.familyDetails$
      .pipe(
        map(res =>
          res.find(m => m.relationshipCode === this.cardHolderRC)
        ),
        switchMap(cardHolder => {
          if (!cardHolder) {
            return EMPTY;
          }

          this.cardHolderDetails = cardHolder;

          // default dates from active coverage
          this.maxDate = new Date(cardHolder.coverage.endDate);
          this.startDateMax = new Date(cardHolder.coverage.endDate);
          this.startDateMin = new Date(cardHolder.coverage.startDate);

          // 🔑 fetch cardholder coverage details
          return this.eligibilityService.getCoverageDetails(
            this.dialogData.memberDetails.clientId,
            this.dialogData.memberDetails.cardId ?? '',
            cardHolder.personCode ?? '',
            cardHolder.empirxMemberId ?? ''
          );
        }),
        tap(res => {
          if (!res) return;

          const futureCoverage =
            res.coverage.find(c => c.eligibilityIndicator === 'F');
          this.cardHoldersFutureCoverage = futureCoverage;
          this.cardHoldersCurrentCoverage = res?.coverage?.find((c) => c.eligibilityIndicator === 'C') ?? null;
          this.setupValidationsForCoverageAddition();
        }),
        finalize(() => this.loader.hideLoader())
      )
      .subscribe();
  }

  private setupValidationsForCoverageAddition(): void {
    const action = this.dialogData.action;

    const hasFutureCoverage = !!this.cardHoldersFutureCoverage;

    if (
      hasFutureCoverage &&
      (action === 'addFutureCoverage' || action === 'updateFutureCoverage')
    ) {
      this.startDateMin = new Date(this.cardHoldersFutureCoverage.startDate);
      this.maxDate = new Date(this.cardHoldersFutureCoverage.endDate);
      this.startDateMax = new Date(this.cardHoldersFutureCoverage.endDate);
      return;
    }

    if (action === 'addCoverage' || action === 'updateCoverage') {
      this.startDateMin = new Date(this.cardHolderDetails.coverage.startDate);
      this.maxDate = new Date(this.cardHolderDetails.coverage.endDate);
      this.startDateMax = new Date();
    }
  }

  // Initializes the dependents stream which reacts to changes in the family details and refresh trigger
  private initDependentsStream(): void {
    this.dependents$ = defer(() =>
      this.refreshDependents.pipe(
        switchMap(() => this.getDependentsStream())
      )
    );
  }

  // Stream that processes family details to extract dependents, handle side effects, and load coverage details
  private getDependentsStream() {
    return this.familyDetails$.pipe(
      map((res) => this.extractDependents(res)),

      tap((dependents) => this.handleDependentsSideEffects(dependents)),

      switchMap((dependents) => this.loadCoverageDetails(dependents)),

      finalize(() => this.loader.hideLoader())
    );
  }

  // Extracts dependents from the family details response, separating cardholder details and filtering out soft-deleted dependents
  private extractDependents(res: any[]) {
    this.cardHolderDetails = res.find(
      m => m.relationshipCode === this.cardHolderRC
    );

    return res.filter(
      i =>
        i.relationshipCode !== this.cardHolderRC &&
        !i.isSoftDeleted
    );
  }
  // Handles side effects related to dependents such as filtering active dependents, showing errors if necessary, setting the data source for the table, and counting total dependents
  private handleDependentsSideEffects(dependents: any[]): void {
    this.filterActiveDependents(dependents);
    this.showActiveDependentsError();
    this.setDataSource(dependents);
    this.totalDependents = this.dataSource.length;
  }

  // Loads coverage details for the cardholder and processes the response to set up future and current coverage information, which is necessary for validation logic
  private loadCoverageDetails(dependents: any[]) {
    return this.eligibilityService
      .getCoverageDetails(
        this.dialogData.memberDetails.clientId,
        this.dialogData.memberDetails.cardId,
        this.cardHolderDetails.personCode,
        this.cardHolderDetails.empirxMemberId
      )
      .pipe(
        tap((res) => this.processCoverageResponse(res)),
        map(() => dependents)
      );
  }
  // Processes the coverage details response to extract the cardholder's future and current coverage information, which is used to set up validations for date fields based on the action being performed
  private processCoverageResponse(res: any): void {
    this.cardHoldersFutureCoverage =
      res.coverage.find(c => c.eligibilityIndicator === 'F');

    this.cardHoldersCurrentCoverage =
      res?.coverage?.find(c => c.eligibilityIndicator === 'C') ?? null;

    this.setupValidations();
  }


  // Sets up validations for the start date and end date fields based on the action being performed and the cardholder's coverage details. This ensures that the user can only select valid dates when adding or updating coverage.
  private setupValidations(): void {
    const action = this.dialogData.action;

    if (action === 'addFutureCoverage' || action === 'updateFutureCoverage') {
      this.startDateMin = this.getDayAfter(this.getCardholderEndDate());
      this.maxDate = new Date('12/31/9999');
      this.startDateMax = new Date('12/31/9999');
      return;
    }

    if (action === 'updateCoverage' || action === 'addCoverage') {
      this.startDateMin = null;
      this.maxDate = this.cardHoldersFutureCoverage?.startDate
        ? this.getDayBefore(this.cardHoldersFutureCoverage.startDate)
        : new Date('12/31/9999');
      this.startDateMax = new Date();
    }
  }

  // Utility method to get phone number based on type (home, cell, work)
  private getDayBefore(date: string | Date): Date {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    return d;
  }

  // Utility method to get the day after a given date, used for setting minimum start date when adding future coverage
  private getDayAfter(date: string | Date): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + 1);
    return d;
  }

  private getCardholderEndDate(): string {
    return this.cardHoldersCurrentCoverage ? this.cardHoldersCurrentCoverage.endDate : null;
  }


  private setDataSource(res: MemberDetails[]): void {
    const termedUsers = res
      .filter(
        (m) => new Date(m.coverage.endDate).valueOf() <
          new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf()
      )
      .sort(
        (a, b) => parseInt(a.personCode) - parseInt(b.personCode)
      );
    const activeUsers = res
      .filter(
        (m) => new Date(m.coverage.endDate).valueOf() >=
          new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf()
      )
      .sort(
        (a, b) => parseInt(a.personCode) - parseInt(b.personCode)
      );
    this.dataSource = [...activeUsers, ...termedUsers];
  }

  private showActiveDependentsError(): void {
    if (this.activeDependents.length !== 0 &&
      this.dialogData.action === 'addCoverage') {
      const errorDialog: MatDialogRef<ErrorComponent> = this.matDialog.open(ErrorComponent, {
        width: '500px',
        data: {
          title: '',
          message: 'There are some active dependent(s). Please terminate the dependent(s) to proceed with adding new coverage',
        },
      });
      errorDialog.afterClosed().subscribe(() => {
        this.dialogRef.close();
      });
    }
  }

  private filterActiveDependents(res: MemberDetails[]): void {
    res.forEach((dep) => {
      dep.relationshipValue =
        AppConstants.RELATIONSHIP_VALUES[dep.relationshipCode];
      if (new Date(dep.coverage.endDate).valueOf() >=
        new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf()) {
        this.activeDependents.push(dep);
      }
    });
  }

  private extractGroupInfo(res: ClientInfoResponse[], result: {}) {
    for (const detail of res) {
      for (const plan of detail.plans) {
        for (const group of plan.groups) {
          if (group.groupId?.toUpperCase() ===
            this.dialogData.memberDetails.coverage.groupNumber?.toUpperCase()) {
            result = {
              groupName: group.groupName,
              groupId: group.groupId,
            };
            break;
          }
        }

        if (Object.keys(result).length > 0) break;
      }
    }
    return result;
  }

  private checkIfLoaderNeedsToBeEnded(): void {
    const actionsRequiringCardHolderCheck = new Set([
      'updateCoverage',
      'addCoverage',
      'addFutureCoverage',
      'updateFutureCoverage'
    ]);

    const requiresCardHolderLogic =
      actionsRequiringCardHolderCheck.has(this.dialogData.action);

    // Hide loader if:
    // 1. Action does NOT require cardholder logic
    // 2. OR member is NOT cardholder
    if (!requiresCardHolderLogic) {
      this.loader.hideLoader();
    }
  }

  private filterGroup(searchTerm: string): Observable<GroupDetails[]> {
    const lowerCaseTerm = searchTerm.trim().toLowerCase();

    return this.listOfGroups$.pipe(
      map((groups) =>
        groups.filter(
          (group) =>
            group.groupName.toLowerCase().includes(lowerCaseTerm) ||
            group.groupId.trim().toLowerCase().includes(lowerCaseTerm) // Case-insensitive and trimmed
        )
      )
    );
  }

  public get minDate(): Date | null {
    const startDate: Date | null =
      this.updateCoverageFormGroup?.controls?.startDate?.getRawValue();
    if (!startDate) return null;

    const { action } = this.dialogData;

    switch (action) {
      case 'updateCoverage':
      case 'updateRx':
      case 'updateFutureCoverage':
        const oneDayPrior = new Date(startDate);
        oneDayPrior.setDate(oneDayPrior.getDate() - 1);
        return oneDayPrior;

      case 'addCoverage':
        return startDate ? this.getDayBefore(startDate) : new Date();

      case 'addFutureCoverage':
        return this.getMinDateForAddFutureCoverage(startDate)

      default:
        return startDate;
    }
  }

  private getMinDateForAddFutureCoverage(startDate: Date): Date {
    let minDate: Date;

    if (startDate) {
      minDate = startDate;
    } else if (
      this.dialogData.memberDetails.relationshipCode === this.cardHolderRC
    ) {
      minDate = this.getDayAfter(this.getCardholderEndDate());
    } else {
      minDate = this.getDayBefore(
        this.cardHoldersFutureCoverage.startDate
      );
    }
    return minDate;
  }

  private getGroupsByClient(clientId: string) {
    this.loader.showLoader();
    this.eligibilityService
      .getClientDetails(clientId)
      .pipe(
        map((res) => {
          const groupsArray = [];
          if (res[0].plans) {
            for (const plan of res[0].plans) {
              if (plan.groups) {
                groupsArray.push(...plan.groups);
              }
            }
          }
          let filteredGroupArray = groupsArray.filter(
            (group) => !!group.groupId
          );
          filteredGroupArray = this.dialogData.action === 'addCoverage' ? this.filterGroupsForDepActiveCoverage(filteredGroupArray) : filteredGroupArray;
          filteredGroupArray = this.dialogData.action === 'addFutureCoverage' ? this.filterGroupsForDepFutureCoverage(filteredGroupArray) : filteredGroupArray;
          return filteredGroupArray;
        }),
        catchError(() => {
          this.loader.hideLoader();
          return of([]);
        })
      )
      .subscribe((res) => {
        this.listOfGroups$ = of(res);
        // Filter clients based on search input
        this.filteredGroups = this.groupFilterControl.valueChanges.pipe(
          startWith(''), // Initially show all clients
          switchMap((searchTerm) => this.filterGroup(searchTerm)) // Filter clients when search term changes
        );
      });
  }

  private filterGroupsForDepActiveCoverage(filteredGroupArray: any[]) {
    if (this.dialogData.memberDetails.relationshipCode !==
      this.cardHolderRC &&
      this.dialogData.cardHolderGroupNumber && this.dialogData.action === 'addCoverage') {
      filteredGroupArray = filteredGroupArray.filter(
        (g) => g.groupId?.toUpperCase() ===
          this.dialogData.cardHolderGroupNumber?.toUpperCase()
      );
    }
    return filteredGroupArray;
  }

  private filterGroupsForDepFutureCoverage(filteredGroupArray: GroupDetails[]) {
    const hasFutureCoverage = !!this.dialogData.cardHoldersFutureCoverage;
    console.log('has future coverage', hasFutureCoverage);
    if (this.dialogData.memberDetails.relationshipCode !==
      this.cardHolderRC && hasFutureCoverage) {
      filteredGroupArray = filteredGroupArray.filter(g => g.groupId?.toUpperCase() === this.dialogData.cardHoldersFutureCoverage.groupId?.toUpperCase());
    }
    return filteredGroupArray;
  }

  private populateFormGroup(dialogData): void {
    this.initialFormGroupValue = {
      groupName:
        this.dialogData.action === 'addRx' ||
          this.dialogData.action === 'updateRx'
          ? this.dialogData.coverage.flexTagName
          : dialogData.groupId,
      startDate:
        this.dialogData.action === 'addRx' ||
          this.dialogData.action === 'updateRx'
          ? new Date(`${dialogData.flexTagStartDate}T00:00:00`)
          : new Date(`${dialogData.startDate}T00:00:00`),
      endDate:
        this.dialogData.action === 'addRx' ||
          this.dialogData.action === 'updateRx'
          ? new Date(`${dialogData.flexTagEndDate}T00:00:00`)
          : new Date(`${dialogData.endDate}T00:00:00`),
    };
    this.updateCoverageFormGroup.setValue(this.initialFormGroupValue);
    this.selectedGroupName = this.dialogData.coverage.groupName;
  }

  public checkIfAnythingUpdated() {
    return (
      JSON.stringify(this.updateCoverageFormGroup.getRawValue()) !==
      JSON.stringify(this.initialFormGroupValue)
    );
  }

  public save(): void {
    this.initializeSave();

    const reqObj: MemberDetails = this.prepareRequest();
    this.trackUpdateCoverageClick(reqObj);

    if (this.isCardHolderUpdateCoverage()) {
      this.updateCardHolderCoverage(reqObj);
      return;
    }

    if (this.isCardHolderAddCoverage()) {
      this.addCardHolderCoverage(reqObj);
      return;
    }

    if (this.isAddFutureCoverage()) {
      this.addCarHolderFutureCoverage(reqObj);
      return;
    }

    if (this.isUpdateFutureCoverage()) {
      this.updateCarHolderFutureCoverage(reqObj);
      return;
    }

    this.addMember(reqObj);
  }


  private initializeSave(): void {
    this.isSaving = true;
    this.loader.showLoader();
    this.loader.showWaitingMessage$.next(
      AppConstants.ERROR.WAIT_MESSAGE_FOR_UPDATE_MEMBER
    );
  }

  private trackUpdateCoverageClick(reqObj: MemberDetails): void {
    this.appInsightsService.trackPageView(
      AppCenterAnalytics.UPDATE_COVERAGE_BUTTON_CLICKED,
      { data: reqObj }
    );
  }

  private isAddFutureCoverage(): boolean {
    return this.dialogData.action === 'addFutureCoverage';
  }

  private isUpdateFutureCoverage(): boolean {
    return this.dialogData.action === 'updateFutureCoverage';
  }


  private isCardHolderUpdateCoverage(): boolean {
    return (
      this.dialogData.memberDetails.relationshipCode === this.cardHolderRC &&
      this.dialogData.action === 'updateCoverage'
    );
  }

  private isCardHolderAddCoverage(): boolean {
    return (
      this.dialogData.memberDetails.relationshipCode === this.cardHolderRC &&
      this.dialogData.action === 'addCoverage'
    );
  }

  private updateCardHolderCoverage(reqObj: MemberDetails): void {
    const reqObject: ICoverageUpdateRequest = {
      cardId: reqObj.cardId,
      clientId: reqObj.clientId,
      empirxMemberId: reqObj.empirxMemberId ?? '',
      modifiedSource: 'BAP',
      endDate: reqObj.terminationDate,
      startDate: reqObj.effectiveDate,
      groupId: reqObj.groupNumber,
      groupName: this.dialogData.coverage.groupName,
    };

    this.eligibilityService.updateMemberCoverage(reqObject).subscribe({
      next: (res) => {
        if (res) {
          this.handleSuccess(() => {
            this.eligibilityService.searchCoverage.next(true);
            this.initialFormGroupValue = this.updateCoverageFormGroup.value;
            this.refreshDependents.next(true);
            this.dialogRef.close();
          });
        }
      },
      error: (err) => {
        this.handleCoverageError(err);
      },
      complete: () => {
        this.createAuditLog(true);
      },
    });
  }

  private addCardHolderCoverage(reqObj: MemberDetails): void {
    const reqObject: ICoverageAddRequest = {
      cardId: reqObj.cardId ?? '',
      clientId: reqObj.clientId,
      empirxMemberId: reqObj.empirxMemberId ?? '',
      modifiedSource: 'BAP',
      endDate: reqObj.terminationDate ?? '',
      startDate: reqObj.effectiveDate ?? '',
      groupId: reqObj.groupNumber ?? '',
      groupName: this.selectedGroupName ?? '',
      patientIds: this.selectedDependents.map((d) => d.patientId ?? ''),
      personCodes: this.selectedDependents.map((d) => d.personCode ?? ''),
    };

    reqObject.patientIds.push(this.dialogData.memberDetails.patientId);
    reqObject.personCodes.push(this.dialogData.memberDetails.personCode);

    this.eligibilityService.addMemberCoverage(reqObject).subscribe({
      next: (res) => {
        if (res) {
          this.handleSuccess(() => {
            this.dialogRef.close();
            this.eligibilityService.searchCoverage.next(true);
          });
        }
      },
      error: (err) => {
        this.handleCoverageError(err);
      },
      complete: () => {
        this.createAuditLog(true);
      },
    });
  }

  private addCarHolderFutureCoverage(reqObj: MemberDetails): void {
    const reqObject: IFutureCoverageRequest = {
      cardId: reqObj.cardId,
      clientId: reqObj.clientId,
      empirxMemberId: reqObj.empirxMemberId ?? '',
      endDate: reqObj.terminationDate,
      startDate: reqObj.effectiveDate,
      groupId: reqObj.groupNumber,
      personCodes: this.selectedDependents.map((d) => d.personCode),
      userName: this.authService.userEmail,
    };
    reqObject.personCodes.push(this.dialogData.memberDetails.personCode);


    this.eligibilityService.addMemberFutureCoverage(reqObject).subscribe({
      next: (res) => {
        if (res) {
          this.handleSuccess(() => {
            this.dialogRef.close();
            this.eligibilityService.searchCoverage.next(true);
          });
        }
      },
      error: (err) => {
        this.handleCoverageError(err);
      },
      complete: () => {
        this.createAuditLog(true);
      },
    });
  }

  private updateCarHolderFutureCoverage(reqObj: MemberDetails): void {
    const reqObject: IFutureCoverageRequest = {
      cardId: reqObj.cardId,
      clientId: reqObj.clientId,
      empirxMemberId: reqObj.empirxMemberId ?? '',
      endDate: reqObj.terminationDate,
      startDate: reqObj.effectiveDate,
      groupId: reqObj.groupNumber,
      userName: this.authService.userEmail,
      isCardHolder: this.dialogData.memberDetails.relationshipCode === this.cardHolderRC
    };
    reqObject.personCode = this.dialogData.memberDetails.personCode;


    this.eligibilityService.updateMemberFutureCoverage(reqObject).subscribe({
      next: (res) => {
        if (res) {
          this.handleSuccess(() => {
            this.dialogRef.close();
            this.eligibilityService.searchCoverage.next(true);
          });
        }
      },
      error: (err) => {
        this.handleCoverageError(err);
      },
      complete: () => {
        this.createAuditLog(true);
      },
    });
  }

  private addMember(reqObj: MemberDetails): void {
    this.eligibilityService.addMember(reqObj).subscribe({
      next: (res) => {
        if (res) {
          this.updatedMember = res;
          this.handleSuccess(() => {
            this.dialogRef.close();
            this.eligibilityService.searchCoverage.next(true);
          });
        }
      },
      error: (err) => {
        this.createAuditLog(false, err);
        const message = getErrorMessage(err);
        this.errorManager.throwError({
          type: ErrorType.ERROR_GENERIC,
          message,
          component: 'Update coverage details',
          title: '',
        });
        this.resetLoaderState();
      },
      complete: () => {
        this.createAuditLog(true);
      },
    });
  }

  private handleSuccess(afterClose: () => void): void {
    this.loader.hideLoader();
    this.isSaving = false;
    this.loader.showWaitingMessage$.next(null);

    const dialogRef: MatDialogRef<ErrorComponent> =
      this.matDialog.open(ErrorComponent, {
        width: '300px',
        data: {
          title: 'Success',
          message: 'Coverage updated successfully',
        },
      });

    dialogRef.afterClosed().subscribe(afterClose);
  }

  private handleCoverageError(err: any): void {
    this.createAuditLog(false, err);

    const message = err.error
      ? JSON.parse(err.error).Message.toLowerCase().trim() !==
        AppConstants.ERROR.CONCURRENT_APPEND_EXCEPTION_MESSAGE.toLowerCase().trim()
        ? JSON.parse(err.error).Message
        : AppConstants.ERROR.END_USER_CONCURRENT_APPEND_EXCEPTION_MESSAGE
      : AppConstants.ERROR.GENERIC_ERROR_MESSAGE;

    this.errorManager.throwError({
      type: ErrorType.ERROR_GENERIC,
      message,
      component: 'Update coverage details',
      title: '',
    });

    this.resetLoaderState();
  }
  private resetLoaderState(): void {
    this.loader.hideLoader();
    this.isSaving = false;
    this.loader.showWaitingMessage$.next(null);
  }

  private prepareRequest(): MemberDetails {
    const reqObj: MemberDetails = {
      patientId: this.dialogData.memberDetails.patientId,
      firstName: this.dialogData.memberDetails.firstName,
      lastName: this.dialogData.memberDetails.lastName,
      middleName: this.dialogData.memberDetails.middleName,
      DOB: this.dialogData.memberDetails.DOB,
      clientId: this.dialogData.memberDetails.clientId,
      gender: this.dialogData.memberDetails.gender,
      email: this.dialogData.memberDetails.email,
      addressLine1: this.dialogData.memberDetails.addressLine1,
      addressLine2: this.dialogData.memberDetails.addressLine2,
      zipCode: this.dialogData.memberDetails.zipCode,
      city: this.dialogData.memberDetails.city,
      state: this.dialogData.memberDetails.state,
      effectiveDate: this.dialogData.memberDetails.coverage.startDate,
      terminationDate: this.dialogData.memberDetails.coverage.endDate,
      homePhone: this.getPhoneNumber('homePhone'),
      cellPhone: this.getPhoneNumber('cellPhone'),
      workPhone: this.getPhoneNumber('workPhone'),
      isUpdated: true,
      empirxMemberId: this.dialogData.memberDetails.empirxMemberId,
      personCode: this.dialogData.memberDetails.personCode,
      relationshipCode: this.dialogData.memberDetails.relationshipCode,
      cardId: this.dialogData.memberDetails.cardId,
      groupNumber: this.updateCoverageFormGroup.controls.groupName.getRawValue(),
      coverageType: this.dialogData.memberDetails.coverage?.coverageType,
      SSN: this.dialogData.memberDetails.SSN,
      flexTagName: this.dialogData.memberDetails.flexTag.flexTagName,
      flexTagStartDate: this.dialogData.memberDetails.flexTag.flexTagStartDate,
      flexTagEndDate: this.dialogData.memberDetails.flexTag.flexTagEndDate,
      flexTagName2: this.dialogData.memberDetails.flexTag2.flexTagName,
      flexTagStartDate2: this.dialogData.memberDetails.flexTag2.flexTagStartDate,
      flexTagEndDate2: this.dialogData.memberDetails.flexTag2.flexTagEndDate,
      flexTagName3: this.dialogData.memberDetails.flexTag3.flexTagName,
      flexTagStartDate3: this.dialogData.memberDetails.flexTag3.flexTagStartDate,
      flexTagEndDate3: this.dialogData.memberDetails.flexTag3.flexTagEndDate,
      flexTagName4: this.dialogData.memberDetails.flexTag4.flexTagName,
      flexTagStartDate4: this.dialogData.memberDetails.flexTag4.flexTagStartDate,
      flexTagEndDate4: this.dialogData.memberDetails.flexTag4.flexTagEndDate,
      flexTagName5: this.dialogData.memberDetails.flexTag5.flexTagName,
      flexTagStartDate5: this.dialogData.memberDetails.flexTag5.flexTagStartDate,
      flexTagEndDate5: this.dialogData.memberDetails.flexTag5.flexTagEndDate,
      flexTagName6: this.dialogData.memberDetails.flexTag6.flexTagName,
      flexTagStartDate6: this.dialogData.memberDetails.flexTag6.flexTagStartDate,
      flexTagEndDate6: this.dialogData.memberDetails.flexTag6.flexTagEndDate,
      clientInternalMemberId: this.dialogData.memberDetails.clientInternalMemberId,
    };
    if (this.dialogData.action === 'addRx' ||
      this.dialogData.action === 'updateRx') {
      reqObj.effectiveDate = this.dialogData.memberDetails.coverage.startDate;
      reqObj.terminationDate = this.dialogData.memberDetails.coverage.endDate;
      reqObj.groupNumber = this.dialogData.memberDetails.coverage.groupNumber;
      reqObj[AppConstants.FLEX_TAG_KEY_MAPPING[this.updateCoverageFormGroup.controls.groupName.getRawValue()][1]] = formatDate(
        new Date(this.updateCoverageFormGroup.controls.startDate.getRawValue())
      );
      reqObj[AppConstants.FLEX_TAG_KEY_MAPPING[this.updateCoverageFormGroup.controls.groupName.getRawValue()][2]] = this.updateCoverageFormGroup.value.endDate
        ? formatDate(new Date(this.updateCoverageFormGroup.value.endDate))
        : '';
      reqObj[AppConstants.FLEX_TAG_KEY_MAPPING[this.updateCoverageFormGroup.controls.groupName.getRawValue()][0]] = this.updateCoverageFormGroup.controls.groupName.getRawValue();
    }
    if (this.dialogData.action === 'addCoverage' ||
      this.dialogData.action === 'updateCoverage' || this.dialogData.action === 'addFutureCoverage' || this.dialogData.action === 'updateFutureCoverage') {
      (reqObj.effectiveDate = formatDate(
        new Date(this.updateCoverageFormGroup.controls.startDate.getRawValue())
      )),
        (reqObj.terminationDate = this.updateCoverageFormGroup.value.endDate
          ? formatDate(new Date(this.updateCoverageFormGroup.value.endDate))
          : '');
    }
    return reqObj;
  }

  public selectRow(dependent: MemberDetails): void {
    const index = this.selectedDependents.indexOf(dependent);
    if (index >= 0) {
      dependent.selected = false;
      this.selectedDependents.splice(index, 1);
    } else {
      dependent.selected = true;
      this.selectedDependents.push(dependent);
    }
  }

  createAuditLog(success: boolean, err?: HttpErrorResponse) {
    let addCoverageSelectedDependents: MemberDetails[] | string[] = [];
    let dependents: MemberDetails[] | string[] = [];
    ({ dependents, addCoverageSelectedDependents } = this.filterOutDependents(dependents, addCoverageSelectedDependents));
    addCoverageSelectedDependents.unshift(
      this.dialogData.memberDetails.personCode
    );
    dependents.unshift(this.dialogData.memberDetails.personCode);
    const reqBody: SaveAuditLogsRequestInterface = {
      clientId: this.dialogData.memberDetails.clientId,
      cardId: this.dialogData.memberDetails.cardId,
      personCd: this.dialogData.memberDetails.personCode,
      firstName: this.dialogData.memberDetails.firstName,
      lastName: this.dialogData.memberDetails.lastName,
      empirxMemberId: this.dialogData.memberDetails.empirxMemberId ?? null,
      emailId:
        this.dialogData.memberDetails.email &&
          this.dialogData.memberDetails.email.toLowerCase() !== 'null'
          ? this.dialogData.memberDetails.email
          : this.authService.userEmail,
      birthDt: this.dialogData.memberDetails.DOB,
      groupNo: this.updateCoverageFormGroup.controls.groupName.getRawValue(),
      groupName: this.selectedGroupName,
      changes:
        this.dialogData.action === 'addCoverage'
          ? AppConstants.AUDIT_LOGS.updateCoverage.action.memberCoverageAdded
            .change
          : AppConstants.AUDIT_LOGS.updateCoverage.action.changedTermedDate
            .change,
      comments:
        this.dialogData.action === 'addCoverage'
          ? this.dialogData.memberDetails.relationshipCode === this.cardHolderRC
            ? `${AppConstants.AUDIT_LOGS.updateCoverage.action.cardHolderCoverageAdded.comments} ${addCoverageSelectedDependents}`
              .replace('%GROUP_NAME%', this.selectedGroupName)
              .replace(
                '%DATE%',
                formatDate(
                  new Date(this.updateCoverageFormGroup.controls.startDate.getRawValue())
                )
              )
            : AppConstants.AUDIT_LOGS.updateCoverage.action.memberCoverageAdded.comments
              .replace('%GROUP_NAME%', this.selectedGroupName)
              .replace(
                '%DATE%',
                formatDate(new Date(this.updateCoverageFormGroup.value.endDate))
                  ? formatDate(new Date(this.updateCoverageFormGroup.value.endDate))
                  : '9999-12-31'
              )
          : this.dialogData.memberDetails.relationshipCode === this.cardHolderRC
            ? `${AppConstants.AUDIT_LOGS.updateCoverage.action.changedTermedDateCardHolder.comments} ${dependents}`
            : AppConstants.AUDIT_LOGS.updateCoverage.action.changedTermedDate
              .comments,
      addedBy: this.authService.userEmail,
      isSuccess: success,
    };
    this.buildCommentBasedOnAction(reqBody);
    this.buildCommentForCoverageUpdate(reqBody);
    this.checkForSuccessOrError(success, err, reqBody);
    this.saveAuditLog(reqBody);
  }

  private filterOutDependents(dependents: string[] | MemberDetails[], addCoverageSelectedDependents: string[] | MemberDetails[]) {
    if (this.totalDependents) {
      dependents = this.dataSource.filter((d) => {
        return (
          new Date(d.coverage.endDate).valueOf() >
          new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf() ||
          (new Date(d.coverage.endDate).valueOf() >=
            new Date(this.updateCoverageFormGroup.value.endDate).valueOf() &&
            new Date(d.coverage.endDate).valueOf() <
            new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf())
        );
      });
      dependents = dependents.map((d) => d.personCode);
    }
    if (this.selectedDependents) {
      addCoverageSelectedDependents = this.selectedDependents.map(
        (d) => d.personCode
      );
    }
    return { dependents, addCoverageSelectedDependents };
  }

  private buildCommentBasedOnAction(
    reqBody: SaveAuditLogsRequestInterface
  ): void {
    const groupName =
      this.updateCoverageFormGroup.controls.groupName.getRawValue();

    if (this.dialogData.action === 'addRx') {
      this.setAuditLogDetails(
        reqBody,
        AppConstants.AUDIT_LOGS.updateCoverage.action.memberNoRxCoverageAdded,
        formatDate(
          new Date(
            this.updateCoverageFormGroup.controls.startDate.getRawValue()
          )
        ),
        groupName
      );
    } else if (this.dialogData.action === 'updateRx') {
      this.setAuditLogDetails(
        reqBody,
        AppConstants.AUDIT_LOGS.updateCoverage.action.memberNoRxCoverageTerminated,
        formatDate(
          new Date(this.updateCoverageFormGroup.value.endDate)
        ),
        groupName
      );
    }
  }

  // Builds the comments for audit logs specifically for coverage updates, handling different scenarios such as adding or updating future coverage and including dependent information in the comments
  private buildCommentForCoverageUpdate(reqBody: SaveAuditLogsRequestInterface): void {
    const dependents = this.selectedDependents.concat(this.dialogData.memberDetails).map(d => d.personCode).join(', ');
    const groupName =
      this.updateCoverageFormGroup.controls.groupName.getRawValue();
    if (this.dialogData.action === 'addFutureCoverage') {
      reqBody.comments = `${AppConstants.AUDIT_LOGS.updateCoverage.action.cardHolderFutureCoverageAdded.comments} ${dependents}`
        .replace('%GROUP_NAME%', groupName)
        .replace(
          '%START_DATE%',
          formatDate(
            new Date(this.updateCoverageFormGroup.controls.startDate.getRawValue())
          )
        )
        .replace(
          '%END_DATE%',
          formatDate(
            new Date(this.updateCoverageFormGroup.controls.endDate.getRawValue())
          )
        );
    }
    else if (this.dialogData.action === 'updateFutureCoverage') {
      reqBody.comments = `${AppConstants.AUDIT_LOGS.updateCoverage.action.cardHolderFutureCoverageEdited.comments} ${dependents}`
        .replace('%GROUP_NAME%', groupName)
        .replace(
          '%START_DATE%',
          formatDate(
            new Date(this.updateCoverageFormGroup.controls.startDate.getRawValue())
          )
        )
        .replace(
          '%END_DATE%',
          formatDate(
            new Date(this.updateCoverageFormGroup.controls.endDate.getRawValue())
          )
        );
    }
  }

  private setAuditLogDetails(
    reqBody: SaveAuditLogsRequestInterface,
    auditConfig: any,
    date: string,
    groupName: string
  ): void {
    reqBody.comments = auditConfig.comments
      .replace('%DATE%', date)
      .replace(
        '%COVERAGE_TYPE%',
        AppConstants.COVERAGE_TYPES[groupName]
      );

    reqBody.changes = auditConfig.change;
  }

  private checkForSuccessOrError(success: boolean, err: HttpErrorResponse, reqBody: SaveAuditLogsRequestInterface) {
    if (!success) {
      const message = err?.error?.Message
        ? err?.error?.Message
        : JSON.parse(err.error)?.Message
          ? JSON.parse(err.error)?.Message
          : JSON.stringify(err);
      (reqBody.httpStatusCode = err.status), (reqBody.errorMsg = message);
    }
  }

  private saveAuditLog(reqBody: SaveAuditLogsRequestInterface) {
    this.auditLogsService.saveAuditLog(reqBody).subscribe({
      next: (res) => {
        this.appInsightsService.trackPageView(
          AppCenterAnalytics.SAVE_AUDIT_LOG_API_SUCCESS,
          { Response: res }
        );
      },
      error: (err) => {
        this.appInsightsService.trackPageView(
          AppCenterAnalytics.SAVE_AUDIT_LOG_API_ERROR,
          { Response: err }
        );
        this.loader.hideLoader();
      },
      complete: () => {
        this.loader.hideLoader();
      },
    });
  }

  onGroupSelection(group: GroupDetails) {
    this.appInsightsService.trackPageView(
      AppCenterAnalytics.UPDATE_COVERAGE_DIALOG_GROUP_SELECTED_FROM_DROPDOWN,
      { Group: group }
    );
    this.selectedGroupName = group.groupName;
  }

  public getPhoneNumber(phoneType: string): string {
    return this.dialogData.memberDetails.phones.filter(
      (p) => p.type === phoneType
    )[0].phoneNumber;
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public checkIfCoverageExpired(element): boolean {
    return (
      new Date(element.coverage.endDate).valueOf() <
      new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf()
    ) && !element.isSoftDeleted;
  }

  public checkIfCoverageIsStartingSoon(element): boolean {
    return (
      new Date(element.coverage.startDate).valueOf() >
      new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf()
    ) && !element.isSoftDeleted;
  }

  public checkIfCoverageSoftDeleted(element): boolean {
    return element.isSoftDeleted;
  }

  public onChangeFlag(): void {
    this.updateCoverageFormGroup.controls.startDate.reset();
    this.updateCoverageFormGroup.controls.endDate.reset();
    const selected =
      AppConstants.FLEX_TAG_KEY_MAPPING[
      this.updateCoverageFormGroup.controls.groupName.getRawValue()
      ];
    const startDate =
      this.dialogData.memberDetails[selected[3]].flexTagStartDate;
    const endDate = this.dialogData.memberDetails[selected[3]].flexTagEndDate;
    const name = this.dialogData.memberDetails[selected[3]].flexTagName;
    this.initialFormGroupValue = {
      groupName: name
        ? name
        : this.updateCoverageFormGroup.controls.groupName.value,
      startDate: startDate
        ? new Date(`${startDate}T00:00:00`)
        : new Date(
          `${this.updateCoverageFormGroup.controls.startDate.getRawValue()}T00:00:00`
        ),
      endDate: endDate
        ? new Date(`${endDate}T00:00:00`)
        : new Date(
          `${this.updateCoverageFormGroup.controls.endDate.value}T00:00:00`
        ),
    };
    this.updateCoverageFormGroup.setValue(this.initialFormGroupValue);
  }
}
